import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  BarChart2,
  Mail,
  MessageSquare,
  Facebook,
  Instagram,
  Phone,
  Globe,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { reportsAPI } from '../../services/api';

interface ChannelAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface ChannelData {
  conversations_by_channel: Record<string, number>;
  conversations_by_status: Record<string, number>;
  daily_breakdown: Array<{
    date: string;
    conversations: number;
    resolved: number;
  }>;
  total_conversations: number;
  resolved_conversations: number;
  avg_first_response_time: number;
}

const getChannelIcon = (channelType: string) => {
  switch (channelType.toLowerCase()) {
    case 'email': return <Mail className="h-4 w-4" />;
    case 'whatsapp': return <Phone className="h-4 w-4" />;
    case 'facebook': return <Facebook className="h-4 w-4" />;
    case 'instagram': return <Instagram className="h-4 w-4" />;
    case 'sms': return <Phone className="h-4 w-4" />;
    case 'website':
    case 'api':
    default: return <MessageSquare className="h-4 w-4" />;
  }
};

const getPerformanceLabel = (index: number): string => {
  if (index === 0) return 'Best';
  if (index === 1) return 'Good';
  if (index === 2) return 'Average';
  return 'Below Avg';
};

const getPerformanceVariant = (index: number): 'secondary' | 'outline' | 'destructive' => {
  if (index <= 1) return 'secondary';
  if (index === 2) return 'outline';
  return 'destructive';
};

export const ChannelAnalytics: React.FC<ChannelAnalyticsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelData, setChannelData] = useState<ChannelData | null>(null);

  useEffect(() => {
    fetchChannelAnalytics();
  }, [dateRange]);

  const fetchChannelAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getConversationReport(params);
      setChannelData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch channel analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!channelData || Object.keys(channelData.conversations_by_channel).length === 0) {
    return (
      <Alert>
        <AlertDescription>No channel analytics data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const channelPerformance = Object.entries(channelData.conversations_by_channel).map(([channel, conversations]) => {
    const resolutionRate = channelData.total_conversations > 0
      ? (conversations / channelData.total_conversations) * (channelData.resolved_conversations / channelData.total_conversations) * 100
      : 0;

    const percentage = channelData.total_conversations > 0
      ? (conversations / channelData.total_conversations) * 100
      : 0;

    return { channel, conversations, percentage, resolutionRate };
  }).sort((a, b) => b.conversations - a.conversations);

  const topChannel = channelPerformance[0];
  const resolutionRatePct = channelData.total_conversations > 0
    ? ((channelData.resolved_conversations / channelData.total_conversations) * 100).toFixed(0)
    : '0';

  const exportData = channelPerformance || [];

  return (
    <div className="space-y-6">

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Channels</CardTitle>
            <BarChart2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{channelPerformance.length}</div>
            <p className="text-xs text-muted-foreground">Active channels</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Channel</CardTitle>
            {getChannelIcon(topChannel?.channel || '')}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{topChannel?.channel || 'N/A'}</div>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">+15.3% · {topChannel?.conversations || 0} conversations</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{channelData.total_conversations.toLocaleString()}</div>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">+8.2% across all channels</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolution Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resolutionRatePct}%</div>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">+5.7% overall performance</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Comparison + Distribution */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Bar breakdown */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Channel Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {channelPerformance.map((ch) => (
                <div key={ch.channel} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{getChannelIcon(ch.channel)}</span>
                      <span className="text-sm font-medium capitalize">{ch.channel}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {ch.conversations} conversations ({ch.percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <Progress value={ch.percentage} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Distribution list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Channel Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {channelPerformance.map((ch) => (
                <div key={ch.channel} className="flex items-center gap-3">
                  <span className="text-muted-foreground">{getChannelIcon(ch.channel)}</span>
                  <span className="flex-1 text-sm capitalize">{ch.channel}</span>
                  <span className="text-sm text-muted-foreground">{ch.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Detailed Channel Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right">Conversations</TableHead>
                <TableHead className="text-right">Percentage</TableHead>
                <TableHead className="text-right">Est. Resolution Rate</TableHead>
                <TableHead className="text-right">Performance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {channelPerformance.map((ch, index) => (
                <TableRow key={ch.channel}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{getChannelIcon(ch.channel)}</span>
                      <span className="text-sm font-medium capitalize">{ch.channel}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {ch.conversations.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {ch.percentage.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Progress value={Math.min(ch.resolutionRate, 100)} className="w-16 h-1.5" />
                      <span className="text-sm w-10">{ch.resolutionRate.toFixed(0)}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={getPerformanceVariant(index)}>
                      {getPerformanceLabel(index)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
